// src/components/HomePage.js
import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AnimatedSearchExample from './AnimatedSearchExample';
import GuidedSearchModal from './GuidedSearchModal';
import { parseNaturalLanguageQuery } from '../utils/naturalLanguageSearch';
import SEO from './SEO';

const HomePage = ({ showLogin }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [rawQuery, setRawQuery] = useState('');
  const [isGuidedModalOpen, setIsGuidedModalOpen] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [usingNaturalLanguage, setUsingNaturalLanguage] = useState(false);

  // Handle natural language search input changes
  const handleNaturalLanguageSearch = (query) => {
    setRawQuery(query);
    
    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Don't process for empty queries
    if (!query.trim()) {
      setUsingNaturalLanguage(false);
      setAppliedFilters(null);
      return;
    }
    
    // For non-empty queries, process after short delay
    searchTimeoutRef.current = setTimeout(() => {
      // Parse the query to display what filters would be applied
      const parsedQuery = parseNaturalLanguageQuery(query);
      
      // Store information about detected filters to show the user
      const detectedFilters = {};
      
      if (parsedQuery.players) {
        detectedFilters.players = parsedQuery.players;
      }
      
      if (parsedQuery.maxTime) {
        detectedFilters.maxTime = parsedQuery.maxTime;
      }
      
      if (parsedQuery.weight) {
        detectedFilters.weight = parsedQuery.weight;
      }
      
      if (parsedQuery.themes && parsedQuery.themes.length > 0) {
        detectedFilters.themes = parsedQuery.themes;
      }
      
      if (parsedQuery.mechanics && parsedQuery.mechanics.length > 0) {
        detectedFilters.mechanics = parsedQuery.mechanics;
      }
      
      if (parsedQuery.searchTerm) {
        detectedFilters.searchTerm = parsedQuery.searchTerm;
      }
      
      setAppliedFilters(detectedFilters);
      setUsingNaturalLanguage(true);
    }, 300);
  };

  // Handle form submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    
    // Clear any pending debounced search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
    
    // Navigate to search page with query if we have one - using nlq parameter
    if (rawQuery.trim()) {
      console.log('navigating to search page with nlq parameter');
      navigate(`/search?nlq=${encodeURIComponent(rawQuery)}`, { replace: true });
    }
  };

  // Handle search from guided modal
  const handleSearchFromGuidedModal = (searchParams) => {
    // Build query string for search page
    console.log("guided homepage");
    const queryParams = new URLSearchParams();
    
    if (searchParams.players) {
      queryParams.set('players', searchParams.players.toString());
    }
    
    if (searchParams.maxTime) {
      queryParams.set('time', searchParams.maxTime.toString());
    }
    
    if (searchParams.themes && searchParams.themes.length > 0) {
      queryParams.set('themes', searchParams.themes.join(','));
    }
    
    // Navigate to search page with params
    navigate(`/search?${queryParams.toString()}&guided=true`, { replace: true });
  };

  return (
    <div className="bg-gray-900 py-8 text-gray-100">
       <SEO 
        title="Find the Best Board Games"
        description="Discover the perfect board game for your group based on interests, time and player count."
      />
      <div className="max-w-6xl mx-auto px-4">
        {/* Search Form */}
        <form onSubmit={handleSearchSubmit}>
          {/* Natural Language Search Bar - Make it HUGE */}
          <div className="flex flex-col items-center justify-end h-[240px] mb-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 sm:mb-8 text-blue-400 text-center mt-[-5vh] sm:mt-[-10vh]">
              Find the best board games
            </h1>
            
            <div className="grid grid-cols-12 items-center gap-2 sm:gap-4 w-full">
              {/* "I want..." - 2 columns */}
              <div className="col-span-12 sm:col-span-2 text-center sm:text-right">
                <span className="text-xl sm:text-3xl text-gray-200 font-medium whitespace-nowrap">I want...</span>
              </div>
              
              {/* Search box - 8 columns */}
              <div className="col-span-12 sm:col-span-8 relative">
                <input
                  type="text"
                  placeholder=""
                  className="w-full p-3 sm:p-5 pl-4 sm:pl-5 rounded-lg border border-gray-700 bg-gray-800 text-gray-100 text-lg sm:text-xl shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={rawQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleNaturalLanguageSearch(value);
                  }}
                />
                {!rawQuery && (
                  <div className="absolute inset-y-0 left-0 flex items-center pointer-events-none pl-4 sm:pl-5 text-gray-400 text-lg sm:text-xl">
                    <AnimatedSearchExample />
                  </div>
                )}
              </div>
              
              {/* Empty space or potential button - 2 columns */}
              <div className="col-span-12 sm:col-span-2">
                {/* This column is intentionally left empty or could contain a search button */}
              </div>
            </div>
          </div>

          {/* "Not sure where to start?" button */}
          <div className="flex justify-center my-4">
            <button
              type="button"
              onClick={() => setIsGuidedModalOpen(true)}
              className="text-blue-400 hover:text-blue-300 flex items-center gap-2 font-medium"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Not sure where to start?
            </button>
          </div>
        </form>

        {/* Applied Natural Language Filters Indicator */}
        <div className="mb-4 mt-0 flex flex-wrap gap-2 items-center">&nbsp;
          {usingNaturalLanguage && appliedFilters && Object.keys(appliedFilters).length > 0 && (
            <>
              {(appliedFilters.players) && (
                <span className="bg-blue-900 text-blue-100 px-2 py-1 rounded-full text-xs font-medium">
                  {appliedFilters.players} players
                </span>
              )}
              
              {appliedFilters.maxTime && (
                <span className="bg-blue-900 text-blue-100 px-2 py-1 rounded-full text-xs font-medium">
                  Under {appliedFilters.maxTime} min
                </span>
              )}
              
              {appliedFilters.weight && (
                <span className="bg-blue-900 text-blue-100 px-2 py-1 rounded-full text-xs font-medium">
                  {appliedFilters.weight} difficulty
                </span>
              )}
              {appliedFilters.themes && appliedFilters.themes.map(theme => (
                <span key={theme} className="bg-green-900 text-green-100 px-2 py-1 rounded-full text-xs font-medium">
                  {theme}
                </span>
              ))}
              
              {appliedFilters.mechanics && appliedFilters.mechanics.map(mechanic => (
                <span key={mechanic} className="bg-purple-900 text-purple-100 px-2 py-1 rounded-full text-xs font-medium">
                  {mechanic}
                </span>
              ))}
              
              {appliedFilters.searchTerm && (
                <span className="bg-gray-700 text-gray-300 px-2 py-1 rounded-full text-xs font-medium">
                  Search: "{appliedFilters.searchTerm}"
                </span>
              )}
            </>
          )}
        </div>

        {/* Recommendation Engine Info Section */}
        <div className="max-w-6xl mx-auto mt-4">
          <div className="border-t border-gray-700 pt-6 mt-1"></div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-blue-400 text-center">Personalized Picks</h2>
          <div className="bg-gray-800 rounded-lg border border-gray-700 p-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="md:w-3/4">
                <p className="text-xl text-gray-300 mb-4">
                  Find board games you'll love with our smart recommendation engine!
                </p>
                <ul className="space-y-2 text-gray-300">
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-400 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Get suggestions based on what you like to play</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-400 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Find games that fit your group size and time limits</span>
                  </li>
                  <li className="flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-green-400 mr-2 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Explore hidden gems loved by other players</span>
                  </li>
                </ul>
              </div>
              <div className="md:w-1/4 flex items-center justify-center">
                {!currentUser ? (
                  <button
                    onClick={() => showLogin()}
                    className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors font-medium"
                  >
                    Sign in to see recommendations
                  </button>
                ) : (
                  <a 
                    href="/recommendations" 
                    className="px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors font-medium"
                  >
                    View my recommendations
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Guided Search Modal */}
        <GuidedSearchModal
          isOpen={isGuidedModalOpen}
          onClose={() => setIsGuidedModalOpen(false)}
          onSearch={handleSearchFromGuidedModal}
        />
      </div>
    </div>
  );
};

export default HomePage;